/**
 * Design constants for category recommendations carousel.
 *
 * Design reference: Figma node 2213:11726
 * Pattern reference: frontend/src/components/activity-details/constants.ts
 */

// ══════════════════════════════════════════════════════════
// Colors
// ══════════════════════════════════════════════════════════

export const CAROUSEL_COLORS = {
  // Card
  cardBackground: '#FFFFFF',
  cardShadow: 'rgba(0, 124, 173, 0.2)',

  // Typography
  titleText: '#1B2831', // Gray 6
  descriptionText: '#323B46', // Gray 5
  metadataText: '#1B2831', // Gray 6

  // Skill recommendation container
  skillContainerBg: '#DBF7F8', // Lightest Blue
  skillTextPrimary: '#007CAD', // Dark Blue

  // Button
  buttonBackground: '#1B2831', // Gray 6
  buttonText: '#FFFFFF',

  // Like badge
  likeBadgeBg: 'rgba(0, 0, 0, 0.3)',
  likeBadgeText: '#FFFFFF',
} as const;

// ══════════════════════════════════════════════════════════
// Typography
// ══════════════════════════════════════════════════════════

export const CAROUSEL_TYPOGRAPHY = {
  sectionTitle: {
    fontFamily: 'DM Sans, sans-serif',
    fontWeight: 700, // Bold
    fontSize: '18px',
    lineHeight: '100%',
    letterSpacing: '-0.72px', // -4%
  },

  cardTitle: {
    fontFamily: 'DM Sans, sans-serif',
    fontWeight: 600, // SemiBold
    fontSize: '20px',
    lineHeight: '100%',
  },

  description: {
    fontFamily: 'DM Sans, sans-serif',
    fontWeight: 400, // Regular
    fontSize: '14px',
    lineHeight: '100%',
  },

  metadata: {
    fontFamily: 'DM Sans, sans-serif',
    fontWeight: 500, // Medium
    fontSize: '12px',
    lineHeight: '100%',
  },

  skillText: {
    fontFamily: 'DM Sans, sans-serif',
    fontWeight: 400, // Regular (bold words use 700)
    fontSize: '12px',
    lineHeight: '140%', // 16.8px - provides space for descenders
  },

  button: {
    fontFamily: 'DM Sans, sans-serif',
    fontWeight: 900, // Black
    fontSize: '12px',
    lineHeight: '100%',
    letterSpacing: '0.72px', // 6%
    textTransform: 'uppercase' as const,
  },

  likeBadge: {
    fontFamily: 'DM Sans, sans-serif',
    fontWeight: 500, // Medium
    fontSize: '12px',
    lineHeight: '100%',
  },
} as const;

// ══════════════════════════════════════════════════════════
// Dimensions (Figma specs)
// ══════════════════════════════════════════════════════════

export const CAROUSEL_DIMENSIONS = {
  // Card dimensions
  cardWidth: 312, // Fixed width per Figma
  cardPadding: 16,
  cardBorderRadius: 24,
  cardGap: 10, // Gap between elements inside card

  // Image
  imageHeight: 180,
  imageBorderRadius: 12,

  // Skill container
  skillContainerBorderRadius: 8,
  skillContainerPaddingY: 16,
  skillContainerPaddingX: 12,
  skillTextMaxHeight: 31, // Fixed height with ellipsis

  // Button
  buttonHeight: 48,
  buttonBorderRadius: 12,
  buttonPaddingX: 16,

  // Carousel container
  carouselGap: 16, // Gap between cards
  sectionGap: 16, // Gap between title and carousel

  // Like badge
  likeBadgeIconSize: 14,
  likeBadgeBorderRadius: 12,
  likeBadgePaddingX: 4,
  likeBadgePaddingY: 4,
  likeBadgeGap: 4,
} as const;

// ══════════════════════════════════════════════════════════
// Responsive Breakpoints
// ══════════════════════════════════════════════════════════

/**
 * Responsive card widths:
 * - Mobile (< 640px): 280px (fits on small screens with margin)
 * - Tablet (640px - 1024px): 312px (Figma spec)
 * - Desktop (>= 1024px): 312px (Figma spec)
 */
export const RESPONSIVE_CARD_WIDTH = {
  mobile: 280,
  tablet: 312,
  desktop: 312,
} as const;

/**
 * Carousel container padding (horizontal):
 * - Mobile: 16px
 * - Tablet: 24px
 * - Desktop: 32px
 */
export const CAROUSEL_PADDING = 'px-4 sm:px-6 lg:px-8';

/**
 * Section title responsive classes
 */
export const SECTION_TITLE_CLASSES = 'text-base sm:text-lg lg:text-xl';
